import {
  Alert,
  Button,
  ButtonGroup,
  DateSelector,
  Flex,
  Modal,
  TicketDetails,
  View,
} from '@tabeo/scarf'
import formError from '@tabeo/sharpei/utils/formError'
import {
  maxLength,
  minLength,
  required,
} from '@tabeo/sharpei/utils/validations'
import FormError from 'components/FormError'
import InteractiveField from 'components/InteractiveField'
import PropTypes from 'prop-types'
import React, { useRef, useState } from 'react'
import { Form, FormSpy } from 'react-final-form'
import { useLocation } from 'react-router'
import { useAuth } from 'resources/Auth'
import { autoValuesByType } from 'resources/Ticket/types'
import Tickets from 'resources/Tickets'
import AssigneeSelector from './Form/AssigneeSelector'
import EntitySelector from './Form/EntitySelector'
import Title from './Form/Title'
import TypeSelector from './Form/TypeSelector'
import validate from './validate'

const { Description } = TicketDetails

function NewTicketModal({ innerRef, onClose }) {
  const {
    data: { user: currentUser },
  } = useAuth()

  const { state = {} } = useLocation()
  const lastAutoValue = useRef({})
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const handleChange = ({ values: { title, description } }) => {
    if (title || description) {
      setHasUnsavedChanges(true)
    } else {
      setHasUnsavedChanges(false)
    }
  }

  return (
    <Modal
      ref={innerRef}
      closable={!hasUnsavedChanges}
      onClose={onClose}
      containerProps={{
        py: [0, undefined],
        mb: [0, undefined],
      }}
      isOpenByDefault
    >
      {({ isOpen, close }) => {
        return isOpen ? (
          <View
            minHeight={['100vh', 'unset']}
            maxWidth={['100%', '820px']}
            mx="auto"
            my={[0, 6]}
            bg="white"
            position="relative"
            borderRadius={[0, 1]}
            boxShadow={[0, 1]}
            p={[5, 10]}
          >
            <Form
              initialValues={{
                owners: [
                  {
                    type: 'admins',
                    id: currentUser.id,
                    name: `${currentUser.firstName} ${currentUser.lastName}`,
                  },
                ],
                type: undefined,
                isStarred: false,
                title: '',
                ...state,
              }}
              validate={validate}
              onSubmit={async values => {
                try {
                  await Tickets.create({
                    ...values,
                    type: values.type || 'default',
                  })
                  close()
                } catch (e) {
                  return formError(e)
                }
              }}
              mutators={{
                setFieldValue: (args, state, tools) => {
                  const [field, value] = args
                  tools.changeValue(state, field, () => value)
                },
              }}
              keepDirtyOnReinitialize
            >
              {({
                handleSubmit,
                submitting,
                errors,
                valid,
                form: {
                  mutators: { setFieldValue },
                },
              }) => (
                <View
                  as="form"
                  onSubmit={handleSubmit}
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  minHeight={['calc(100vh - 40px)', '100%']}
                >
                  <TicketDetails>
                    <View width="100%">
                      <Flex
                        flexWrap="wrap"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        mb={3}
                      >
                        <InteractiveField
                          label="Type"
                          name="type"
                          component={TypeSelector}
                          containerProps={{ mb: 0, mt: 3, mr: 3 }}
                        />
                        <InteractiveField
                          label="Assignees"
                          name="owners"
                          component={AssigneeSelector}
                          containerProps={{ mb: 0, mt: 3, mr: 3 }}
                        />
                        <InteractiveField
                          label="Due date"
                          name="overdueAt"
                          component={DateSelector}
                          containerProps={{ mb: 0, mt: 3 }}
                          hasTime
                        />
                      </Flex>
                      <InteractiveField
                        name="title"
                        component={Title}
                        variant="filled"
                        containerProps={{ mb: 3 }}
                        validate={[required, minLength(3), maxLength(100)]}
                      />
                      <InteractiveField
                        name="description"
                        component={Description}
                        variant="filled"
                        containerProps={{ mb: 3 }}
                      />
                      <View>
                        <View>
                          {errors?.links?.map((error, i) => (
                            <Alert
                              key={i}
                              status="warning"
                              _notLast={{ mb: 2 }}
                            >
                              <Alert.Icon />
                              {error}
                            </Alert>
                          ))}
                        </View>

                        <InteractiveField
                          label="Links"
                          name="entities"
                          component={EntitySelector}
                          containerProps={{ mt: 6 }}
                        />
                      </View>
                    </View>
                  </TicketDetails>
                  <ButtonGroup mt={5}>
                    <Button variant="secondary" type="button" onClick={close}>
                      Cancel
                    </Button>
                    <Button loading={submitting} disabled={!valid}>
                      Create
                    </Button>
                  </ButtonGroup>
                  <FormError />
                  <FormSpy
                    subscription={{
                      active: true,
                      touched: true,
                      values: true,
                      initialValues: true,
                    }}
                    onChange={({ active, touched, values, initialValues }) => {
                      const autoValues = {
                        title: '',
                        description: '',
                        ...initialValues,
                        ...(autoValuesByType[values.type] || {}),
                      }

                      Object.entries(autoValues).forEach(([name, value]) => {
                        if (
                          active !== name &&
                          (!touched[name] ||
                            values[name] === initialValues[name] ||
                            values[name] === lastAutoValue.current[name])
                        ) {
                          if (values[name] !== value) {
                            lastAutoValue.current[name] = value
                            setFieldValue(name, value)
                          }
                        }
                      })
                    }}
                  />
                  <FormSpy
                    subscription={{
                      values: true,
                    }}
                    onChange={handleChange}
                  />
                </View>
              )}
            </Form>
          </View>
        ) : null
      }}
    </Modal>
  )
}

NewTicketModal.propTypes = {
  onClose: PropTypes.func,
  innerRef: PropTypes.object.isRequired,
}

export default NewTicketModal
